/**
 * ============================================================================
 * VENDOR SHORTLISTING ASSISTANT - Multi-Step Vendor Selection Tool
 * ============================================================================
 * 
 * PURPOSE:
 * Helps couples organize and compare vendors across categories with a
 * structured 3-step workflow. Allows filtering, sorting, prioritizing,
 * and side-by-side comparison of vendors for informed decision-making.
 * 
 * KEY FEATURES:
 * 1. 3-Step Workflow - Browse → Shortlist → Compare
 * 2. Category-Based Browsing - Filter by vendor type
 * 3. Advanced Filtering - Location, price range, style
 * 4. Priority Levels - Mark vendors as Must-Have, High, Medium, Low priority
 * 5. Side-by-Side Comparison - Compare up to 3 vendors simultaneously
 * 6. Vendor Profiles - Detailed vendor information modal
 * 7. Shortlist Management - Add/remove vendors, save shortlist
 * 
 * 3-STEP WORKFLOW:
 * 
 * STEP 1: Browse Vendors
 * - Select vendor category (Venue, Photography, etc.)
 * - Apply filters (location, price, style)
 * - Sort by rating, price, or name
 * - View vendor cards with key details
 * - Add vendors to shortlist
 * 
 * STEP 2: Manage Shortlist
 * - View all shortlisted vendors
 * - Set priority levels (Must-Have to Low)
 * - Remove vendors from shortlist
 * - Organize by category
 * - Save shortlist to dashboard
 * 
 * STEP 3: Compare Vendors
 * - Select 2-3 vendors to compare
 * - Side-by-side comparison table
 * - Compare: pricing, services, ratings, availability
 * - Make final decisions
 * 
 * BACKEND INTEGRATION:
 * - GET /api/vendors - Fetch all vendors
 * - GET /api/vendors/category/:category - Filter by category
 * - GET /api/vendors/:id - Vendor details
 * - POST /api/shortlist/save - Save shortlist
 * - GET /api/shortlist - Load saved shortlist
 * 
 * STATE MANAGEMENT:
 * - currentStep: Active step (1, 2, or 3)
 * - shortlist: Array of shortlisted vendors
 * - vendorPriorities: Priority levels for each vendor
 * - selectedCategory: Active vendor category
 * - filters: Location, price range, style filters
 * - sortBy: Sort criteria (rating, price, name)
 * 
 * PRIORITY LEVELS:
 * - Must-Have (5 stars) - Essential vendors
 * - High (4 stars) - Very important
 * - Medium (3 stars) - Standard priority
 * - Low (2 stars) - Nice to have
 * 
 * ============================================================================
 */

import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/apiService';
import AuthService from '../../services/authService';
import NotificationModal from '../../components/NotificationModal';
import VendorProfileModal from '../../components/VendorProfileModal';
import VendorGrid from '../../components/VendorGrid';
import { API_ENDPOINTS } from '../../config/api';
import BackgroundIcons from '../../components/BackgroundIcons';
import { formatVendorForModal } from '../../utils/vendorHelpers';

const VendorShortlistingAssistant = () => {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  
  // ------------------------------------------------------------------------
  // WORKFLOW STATE
  // ------------------------------------------------------------------------
  const [currentStep, setCurrentStep] = useState(1);
  // Current step in the 4-step workflow
  // 1: Choose Category
  // 2: Browse Vendors
  // 3: View Profile
  // 4: Add to Shortlist
  
  // ------------------------------------------------------------------------
  // SHORTLIST STATE
  // ------------------------------------------------------------------------
  const [shortlist, setShortlist] = useState([]);
  // Array of vendors added to shortlist
  // Each vendor: { id, name, category, rating, price, ... }
  // Persists across category changes
  
  const [vendorPriorities, setVendorPriorities] = useState({});
  // Priority levels for each shortlisted vendor
  // Structure: { vendorId: priorityLevel }
  // Priority levels: 5 (Must-Have), 4 (High), 3 (Medium), 2 (Low)
  
  // ------------------------------------------------------------------------
  // PRIORITY MODAL STATE
  // ------------------------------------------------------------------------
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  // Priority assignment modal visibility
  // true = modal open
  // false = modal closed
  
  const [selectedVendorForPriority, setSelectedVendorForPriority] = useState(null);
  // Vendor selected for priority assignment
  // null = no vendor selected
  // object = vendor data
  
  const [selectedPriority, setSelectedPriority] = useState(3);
  // Currently selected priority level in modal
  // Default: 3 (Medium)
  // Range: 2-5 (Low to Must-Have)
  
  // ------------------------------------------------------------------------
  // VENDOR BROWSING STATE
  // ------------------------------------------------------------------------
  const [currentVendors, setCurrentVendors] = useState([]);
  // Vendors displayed in current view (filtered and sorted)
  // Updated when category, filters, or sort changes
  
  const [allVendors, setAllVendors] = useState([]);
  // All vendors loaded from database
  // Used as source for filtering
  
  const [selectedCategory, setSelectedCategory] = useState('');
  // Currently selected vendor category
  // '' = no category selected (Step 1)
  // 'Venue', 'Photography', etc. = category selected (Step 2+)
  
  // ------------------------------------------------------------------------
  // UI STATE
  // ------------------------------------------------------------------------
  const [isVisible, setIsVisible] = useState(false);
  // Page visibility animation state
  // Used for fade-in animation on mount
  
  const [loading, setLoading] = useState(false);
  // Loading state during vendor fetch
  // true = showing loading animation
  // false = ready for interaction
  
  const [saving, setSaving] = useState(false);
  // Saving state during shortlist save
  // true = showing "Saving..." on button
  // false = ready to save
  
  // ------------------------------------------------------------------------
  // FILTER AND SORT STATE
  // ------------------------------------------------------------------------
  const [filters, setFilters] = useState({
    location: '',      // Location filter (e.g., 'Downtown', 'Suburbs')
    priceRange: '',    // Price range filter (e.g., '$', '$$', '$$$')
    style: ''          // Style filter (e.g., 'Classic', 'Modern', 'Rustic')
  });
  
  const [sortBy, setSortBy] = useState('rating');
  // Sort criteria for vendor list
  // Options: 'rating' (high to low), 'price' (low to high), 'name' (A-Z)
  
  const [filterOptions, setFilterOptions] = useState({
    locations: [],     // Available location options (extracted from vendors)
    priceRanges: [],   // Available price range options
    styles: []         // Available style options
  });
  
  // ------------------------------------------------------------------------
  // VENDOR PROFILE MODAL STATE
  // ------------------------------------------------------------------------
  const [selectedVendor, setSelectedVendor] = useState(null);
  // Vendor selected for profile modal
  // null = no vendor selected
  // object = vendor data for modal display
  
  const [showProfile, setShowProfile] = useState(false);
  // Vendor profile modal visibility
  // true = modal open
  // false = modal closed
  
  // ------------------------------------------------------------------------
  // NOTIFICATION STATE
  // ------------------------------------------------------------------------
  const [notification, setNotification] = useState({
    isOpen: false,      // Modal visibility
    type: 'error',      // Notification type: 'success', 'error', 'info'
    title: '',          // Notification title
    message: ''         // Notification message
  });

  // ============================================================================
  // WORKFLOW STEPS CONFIGURATION
  // ============================================================================
  // 4-step process for vendor shortlisting
  const steps = [
    { id: 1, title: 'Choose Category', icon: '🎯' },
    { id: 2, title: 'Browse Vendors', icon: '👀' },
    { id: 3, title: 'View Profile', icon: '📋' },
    { id: 4, title: 'Add to Shortlist', icon: '✅' }
  ];

  // ============================================================================
  // VENDOR CATEGORIES CONFIGURATION
  // ============================================================================
  // Available vendor categories with icons and color themes
  const categories = [
    { name: 'Venue', icon: '🏛️', color: 'from-rose-100 to-pink-200' },
    { name: 'Catering', icon: '🍽️', color: 'from-champagne-100 to-gold-200' },
    { name: 'Photography', icon: '📸', color: 'from-purple-100 to-lavender-200' },
    { name: 'Flowers', icon: '🌸', color: 'from-pink-100 to-rose-200' },
    { name: 'Music', icon: '🎵', color: 'from-blue-100 to-indigo-200' },
    { name: 'Attire', icon: '👗', color: 'from-mauve-100 to-purple-200' },
    { name: 'Transportation', icon: '🚗', color: 'from-teal-100 to-cyan-200' },
    { name: 'Planning', icon: '📋', color: 'from-blush-100 to-pink-200' }
  ];

  // ============================================================================
  // VENDOR DATABASE
  // ============================================================================
  // Mock vendor data (in production, this would come from backend API)
  const vendorDatabase = [
    // Venue vendors
    {
      id: 1,
      name: 'Elegant Venues Co.',
      category: 'Venue',
      tagline: 'Creating magical moments in stunning locations',
      image: '🏛️',
      rating: 4.9,
      reviews: 127,
      price: '$$$',
      location: 'Downtown',
      style: 'Classic',
      highlights: ['Historic Architecture', 'Garden Views', 'Full Service'],
      description: 'Premier wedding venue with historic architecture and modern amenities. Our grand ballroom can accommodate up to 300 guests.'
    },
    {
      id: 2,
      name: 'Garden Paradise Estate',
      category: 'Venue',
      tagline: 'Where nature meets elegance',
      image: '🏛️',
      rating: 4.8,
      reviews: 98,
      price: '$$$$',
      location: 'Garden District',
      style: 'Romantic',
      highlights: ['Outdoor Ceremonies', 'Manicured Gardens', 'Vintage Charm'],
      description: 'Breathtaking outdoor venue with manicured gardens and vintage charm. Perfect for intimate ceremonies.'
    },
    {
      id: 3,
      name: 'Modern Loft Spaces',
      category: 'Venue',
      tagline: 'Contemporary elegance in the heart of the city',
      image: '🏛️',
      rating: 4.7,
      reviews: 85,
      price: '$$',
      location: 'Downtown',
      style: 'Modern',
      highlights: ['Industrial Chic', 'City Views', 'Flexible Layout'],
      description: 'Sleek industrial loft with floor-to-ceiling windows and customizable lighting.'
    },
    // Catering vendors
    {
      id: 4,
      name: 'Gourmet Delights Catering',
      category: 'Catering',
      tagline: 'Culinary excellence for your special day',
      image: '🍽️',
      rating: 4.7,
      reviews: 156,
      price: '$$$',
      location: 'Citywide',
      style: 'Classic',
      highlights: ['Farm-to-Table', 'Custom Menus', 'Wine Pairing'],
      description: 'Award-winning catering service specializing in farm-to-table cuisine and elegant presentation.'
    },
    {
      id: 5,
      name: 'Artisan Kitchen Co.',
      category: 'Catering',
      tagline: 'Handcrafted cuisine with love',
      image: '🍽️',
      rating: 4.6,
      reviews: 92,
      price: '$$',
      location: 'Suburbs',
      style: 'Rustic',
      highlights: ['Local Ingredients', 'Family Style', 'Comfort Food'],
      description: 'Boutique catering focusing on locally-sourced ingredients and artisanal preparation.'
    },
    // Photography vendors
    {
      id: 6,
      name: 'Capture Moments Studio',
      category: 'Photography',
      tagline: 'Preserving your precious memories forever',
      image: '📸',
      rating: 4.9,
      reviews: 156,
      price: '$$$',
      location: 'Citywide',
      style: 'Modern',
      highlights: ['Artistic Style', 'Full Day Coverage', 'Quick Delivery'],
      description: 'Professional wedding photography with artistic flair. Specializing in candid moments and stunning portraits.'
    },
    {
      id: 7,
      name: 'Timeless Photography',
      category: 'Photography',
      tagline: 'Classic elegance in every frame',
      image: '📸',
      rating: 4.8,
      reviews: 134,
      price: '$$$$',
      location: 'Downtown',
      style: 'Classic',
      highlights: ['Luxury Packages', 'Same Day Editing', 'Drone Photography'],
      description: 'Luxury wedding photography with a timeless approach. Known for elegant compositions.'
    },
    {
      id: 8,
      name: 'Creative Lens Photography',
      category: 'Photography',
      tagline: 'Artistic vision meets wedding magic',
      image: '📸',
      rating: 4.7,
      reviews: 78,
      price: '$$',
      location: 'Garden District',
      style: 'Bohemian',
      highlights: ['Creative Editing', 'Film Photography', 'Natural Light'],
      description: 'Creative and artistic wedding photography with a bohemian touch.'
    },
    // Flowers vendors
    {
      id: 9,
      name: 'Bloom & Blossom Florals',
      category: 'Flowers',
      tagline: 'Bringing your floral dreams to life',
      image: '🌸',
      rating: 4.8,
      reviews: 89,
      price: '$$',
      location: 'Garden District',
      style: 'Romantic',
      highlights: ['Seasonal Flowers', 'Custom Arrangements', 'Eco-Friendly'],
      description: 'Full-service floral design specializing in romantic arrangements and seasonal blooms.'
    },
    {
      id: 10,
      name: 'Wildflower Designs',
      category: 'Flowers',
      tagline: 'Natural beauty in every arrangement',
      image: '🌸',
      rating: 4.6,
      reviews: 67,
      price: '$',
      location: 'Suburbs',
      style: 'Bohemian',
      highlights: ['Wildflowers', 'Dried Arrangements', 'Sustainable'],
      description: 'Eco-friendly floral design using locally-sourced and wildflowers for a natural look.'
    },
    // Music vendors
    {
      id: 11,
      name: 'Harmony Wedding Music',
      category: 'Music',
      tagline: 'Setting the perfect soundtrack for love',
      image: '🎵',
      rating: 4.9,
      reviews: 112,
      price: '$$$',
      location: 'Citywide',
      style: 'Classic',
      highlights: ['Live Musicians', 'DJ Services', 'Sound Equipment'],
      description: 'Professional wedding musicians offering everything from ceremony strings to reception DJs.'
    },
    {
      id: 12,
      name: 'Acoustic Dreams',
      category: 'Music',
      tagline: 'Intimate acoustic performances',
      image: '🎵',
      rating: 4.7,
      reviews: 45,
      price: '$$',
      location: 'Garden District',
      style: 'Romantic',
      highlights: ['Acoustic Guitar', 'Vocal Performances', 'Custom Songs'],
      description: 'Acoustic duo specializing in intimate ceremony music and cocktail hour entertainment.'
    },
    // Attire vendors
    {
      id: 13,
      name: 'Bridal Elegance Boutique',
      category: 'Attire',
      tagline: 'Where dreams become reality',
      image: '👗',
      rating: 4.8,
      reviews: 203,
      price: '$$$',
      location: 'Downtown',
      style: 'Classic',
      highlights: ['Designer Gowns', 'Custom Alterations', 'Styling'],
      description: 'Premier bridal boutique featuring designer gowns and personalized styling services.'
    },
    {
      id: 14,
      name: 'Modern Bride Collective',
      category: 'Attire',
      tagline: 'Contemporary style for the modern bride',
      image: '👗',
      rating: 4.6,
      reviews: 87,
      price: '$$',
      location: 'Suburbs',
      style: 'Modern',
      highlights: ['Modern Designs', 'Separates', 'Non-Traditional'],
      description: 'Trendy bridal boutique specializing in contemporary designs and non-traditional wedding attire.'
    },
    // Transportation vendor
    {
      id: 15,
      name: 'Luxury Wedding Transport',
      category: 'Transportation',
      tagline: 'Arrive in style on your special day',
      image: '🚗',
      rating: 4.7,
      reviews: 94,
      price: '$$$',
      location: 'Citywide',
      style: 'Classic',
      highlights: ['Luxury Cars', 'Professional Chauffeurs', 'Vintage Options'],
      description: 'Premium wedding transportation service featuring luxury vehicles and professional chauffeurs.'
    }
  ];

  const showNotification = (type, title, message) => {
    setNotification({
      isOpen: true,
      type,
      title,
      message
    });
  };

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, isOpen: false }));
  };

  const handleVendorClick = (vendor) => {
    console.log('🔍 Vendor clicked:', vendor);
    const profileData = formatVendorForModal(vendor);
    setSelectedVendor(profileData);
    setShowProfile(true);
  };

  useEffect(() => {
    setIsVisible(true);
    
    // Fetch vendors from API instead of using hardcoded data
    const fetchVendors = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_ENDPOINTS.vendors}/all`);
        const data = await response.json();
        
        if (data.success && data.vendors) {
          setAllVendors(data.vendors);
          
          // Extract unique filter options from vendors
          const locations = [...new Set(data.vendors.map(v => v.location).filter(Boolean))];
          const priceRanges = [...new Set(data.vendors.map(v => v.price).filter(Boolean))];
          const styles = [...new Set(data.vendors.map(v => v.style).filter(Boolean))];
          
          setFilterOptions({
            locations: locations.sort(),
            priceRanges: priceRanges.sort(),
            styles: styles.sort()
          });
          
          console.log(`✅ Loaded ${data.vendors.length} vendors from Aurora`);
          console.log('📊 Filter options:', { locations, priceRanges, styles });
        } else {
          // Fallback to hardcoded data if API fails
          console.warn('⚠️ API failed, using fallback vendor data');
          setAllVendors(vendorDatabase);
          
          // Extract filter options from fallback data
          const locations = [...new Set(vendorDatabase.map(v => v.location).filter(Boolean))];
          const priceRanges = [...new Set(vendorDatabase.map(v => v.price).filter(Boolean))];
          const styles = [...new Set(vendorDatabase.map(v => v.style).filter(Boolean))];
          
          setFilterOptions({
            locations: locations.sort(),
            priceRanges: priceRanges.sort(),
            styles: styles.sort()
          });
        }
      } catch (error) {
        console.error('❌ Error fetching vendors:', error);
        // Fallback to hardcoded data
        setAllVendors(vendorDatabase);
        
        // Extract filter options from fallback data
        const locations = [...new Set(vendorDatabase.map(v => v.location).filter(Boolean))];
        const priceRanges = [...new Set(vendorDatabase.map(v => v.price).filter(Boolean))];
        const styles = [...new Set(vendorDatabase.map(v => v.style).filter(Boolean))];
        
        setFilterOptions({
          locations: locations.sort(),
          priceRanges: priceRanges.sort(),
          styles: styles.sort()
        });
      } finally {
        setLoading(false);
      }
    };

    fetchVendors();
    
    let lastAuthState = !!localStorage.getItem('token');
    
    const checkAuthAndLoadData = () => {
      const token = localStorage.getItem('token');
      const currentAuthState = !!token;
      
      if (!token) {
        // User is not authenticated - load guest shortlist
        const guestShortlist = AuthService.getGuestShortlist();
        if (guestShortlist.length > 0) {
          // Convert guest shortlist back to component format
          const convertedShortlist = guestShortlist.map(item => ({
            id: item.id,
            name: item.vendor_name || item.name,
            category: item.vendor_category || item.category,
            rating: item.vendor_data?.rating || item.rating,
            location: item.vendor_data?.location || item.location,
            price: item.vendor_data?.price || item.price,
            image: item.vendor_data?.image || item.image,
            tagline: item.vendor_data?.tagline || item.tagline,
            highlights: item.vendor_data?.highlights || item.highlights,
            description: item.vendor_data?.description || item.description
          }));
          
          // Convert priorities
          const priorities = {};
          guestShortlist.forEach(item => {
            priorities[item.id] = item.priority || 3;
          });
          
          setShortlist(convertedShortlist);
          setVendorPriorities(priorities);
        } else {
          // No guest data - start fresh
          setShortlist([]);
          setVendorPriorities({});
        }
      } else {
        // User is authenticated - start fresh (guest data already migrated)
        setShortlist([]);
        setVendorPriorities({});
      }
      
      lastAuthState = currentAuthState;
    };

    const checkAuthStateChange = () => {
      const currentAuthState = !!localStorage.getItem('token');
      // Only reload data if auth state actually changed
      if (currentAuthState !== lastAuthState) {
        checkAuthAndLoadData();
      }
    };

    checkAuthAndLoadData();
    
    // Listen for focus events to detect when user returns to page after logout
    const handleFocus = () => {
      checkAuthStateChange();
    };
    
    // Listen for storage changes (when user logs in/out in another tab)
    const handleStorageChange = () => {
      checkAuthStateChange();
    };
    
    window.addEventListener('focus', handleFocus);
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const getShortlistSummary = () => {
    if (shortlist.length === 0) return 'No vendors selected';
    
    const categoryCounts = shortlist.reduce((acc, vendor) => {
      const category = vendor.category.toLowerCase();
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    }, {});
    
    const categories = Object.keys(categoryCounts);
    
    if (categories.length === 1) {
      // Single category
      const category = categories[0];
      const count = categoryCounts[category];
      return `${count} ${category} vendor${count > 1 ? 's' : ''}`;
    } else {
      // Multiple categories
      const total = shortlist.length;
      return `${total} vendor${total > 1 ? 's' : ''} from ${categories.length} categories`;
    }
  };

  const updateVendorPriority = (vendor) => {
    setSelectedVendorForPriority(vendor);
    setSelectedPriority(vendorPriorities[vendor.id] || 3);
    setShowPriorityModal(true);
  };

  const confirmPriorityUpdate = () => {
    if (selectedVendorForPriority) {
      setVendorPriorities(prev => ({
        ...prev,
        [selectedVendorForPriority.id]: selectedPriority
      }));
    }
    setShowPriorityModal(false);
    setSelectedVendorForPriority(null);
  };

  const nextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const selectCategory = (category) => {
    setLoading(true);
    setSelectedCategory(category);
    
    // Filter vendors by selected category
    setTimeout(() => {
      const filteredVendors = allVendors.filter(v => v.category === category);
      setCurrentVendors(filteredVendors);
      setLoading(false);
      setCurrentStep(2); // Go directly to grid view
    }, 800);
  };

  const applyFilters = (vendors) => {
    let filtered = vendors;

    // Apply location filter
    if (filters.location) {
      filtered = filtered.filter(v => 
        v.location && v.location.toLowerCase().includes(filters.location.toLowerCase())
      );
    }

    // Apply price range filter
    if (filters.priceRange) {
      filtered = filtered.filter(v => v.price === filters.priceRange);
    }

    // Apply style filter
    if (filters.style) {
      filtered = filtered.filter(v => 
        v.style && v.style.toLowerCase().includes(filters.style.toLowerCase())
      );
    }

    return filtered;
  };

  const applySorting = (vendors) => {
    const sorted = [...vendors];
    
    switch (sortBy) {
      case 'rating':
        return sorted.sort((a, b) => (b.rating || 0) - (a.rating || 0));
      case 'price-low':
        return sorted.sort((a, b) => {
          const priceOrder = { '$': 1, '$$': 2, '$$$': 3, '$$$$': 4 };
          return (priceOrder[a.price] || 0) - (priceOrder[b.price] || 0);
        });
      case 'price-high':
        return sorted.sort((a, b) => {
          const priceOrder = { '$': 1, '$$': 2, '$$$': 3, '$$$$': 4 };
          return (priceOrder[b.price] || 0) - (priceOrder[a.price] || 0);
        });
      case 'reviews':
        return sorted.sort((a, b) => (b.reviews || 0) - (a.reviews || 0));
      default:
        return sorted;
    }
  };

  const getFilteredAndSortedVendors = () => {
    const categoryVendors = allVendors.filter(v => v.category === selectedCategory);
    const filtered = applyFilters(categoryVendors);
    return applySorting(filtered);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleSortChange = (value) => {
    setSortBy(value);
  };

  const clearFilters = () => {
    setFilters({
      location: '',
      priceRange: '',
      style: ''
    });
    setSortBy('rating');
  };

  const removeFromShortlist = (vendorId) => {
    setShortlist(prev => prev.filter(v => v.id !== vendorId));
    setVendorPriorities(prev => {
      const updated = { ...prev };
      delete updated[vendorId];
      return updated;
    });
    
    // Also remove from guest shortlist if user is not authenticated
    const token = localStorage.getItem('token');
    if (!token) {
      AuthService.removeFromGuestShortlist(vendorId);
    }
  };

  const handleFinalizeClick = () => {
    // Proceed directly to finalize
    finalizeSelection();
  };

  const finalizeSelection = async () => {
    if (shortlist.length === 0) {
      showNotification('warning', 'No Vendors Selected', 'Please add at least one vendor to your shortlist before finalizing.');
      return;
    }
    
    setSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        // Guest mode - save to localStorage using authService
        shortlist.forEach(vendor => {
          const vendorData = {
            id: vendor.id,
            vendor_name: vendor.name,
            vendor_category: vendor.category,
            vendor_data: {
              name: vendor.name,
              category: vendor.category,
              rating: vendor.rating,
              location: vendor.location,
              price: vendor.price,
              image: vendor.image,
              tagline: vendor.tagline,
              highlights: vendor.highlights,
              description: vendor.description
            },
            notes: vendor.tagline,
            priority: vendorPriorities[vendor.id] || 3
          };
          
          AuthService.addToGuestShortlist(vendorData);
        });
        
        const categoryName = selectedCategory;
        const vendorCount = shortlist.length;
        
        showNotification('success', 'Shortlist Saved!', `${vendorCount} ${categoryName.toLowerCase()} vendor${vendorCount > 1 ? 's' : ''} have been saved to your shortlist. Sign up or log in to save them permanently to your dashboard!`);
        
        // Don't reset state for guest users - keep shortlist visible until they login
        // Only reset step to allow continuing with more categories
        setCurrentStep(1);
        setSelectedCategory('');
        setCurrentVendors([]);
        
        return;
      }
      
      // Authenticated mode - save to database
      const results = [];
      const errors = [];
      const priorityUpdates = [];
      const samePriority = [];
      
      for (const vendor of shortlist) {
        try {
          const response = await fetch(`${API_ENDPOINTS.vendors}/shortlist/add`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              vendor_name: vendor.name,
              vendor_category: vendor.category.toLowerCase(),
              vendor_data: {
                id: vendor.id,
                name: vendor.name,
                category: vendor.category,
                tagline: vendor.tagline,
                rating: vendor.rating,
                reviews: vendor.reviews,
                price: vendor.price,
                location: vendor.location,
                style: vendor.style,
                highlights: vendor.highlights,
                description: vendor.description
              },
              notes: vendor.tagline,
              priority: vendorPriorities[vendor.id] || 3
            })
          });

          if (response.ok) {
            results.push(vendor.name);
          } else {
            // Check if vendor already exists (400/409 error)
            if (response.status === 409 || response.status === 400) {
              console.log('🔄 Vendor exists, checking priority for:', vendor.name);
              
              // Get current shortlist to check existing priority
              try {
                const checkResponse = await fetch(`${API_ENDPOINTS.vendors}/shortlist`, {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  }
                });
                
                if (checkResponse.ok) {
                  const shortlistData = await checkResponse.json();
                  const existingVendor = shortlistData.shortlists?.find(v => 
                    v.vendor_name === vendor.name
                  );
                  
                  const currentPriority = existingVendor?.priority || 3;
                  const newPriority = vendorPriorities[vendor.id] || 3;
                  
                  if (currentPriority === newPriority) {
                    // Same priority - just note it
                    samePriority.push({
                      name: vendor.name,
                      priority: newPriority === 5 ? 'High' : newPriority === 3 ? 'Medium' : 'Low'
                    });
                  } else {
                    // Different priority - update it
                    const updateResponse = await fetch(`${API_ENDPOINTS.vendors}/shortlist/update-priority`, {
                      method: 'PUT',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                      },
                      body: JSON.stringify({
                        vendor_name: vendor.name,
                        vendor_category: vendor.category.toLowerCase(),
                        priority: newPriority
                      })
                    });

                    if (updateResponse.ok) {
                      console.log('✅ Priority updated successfully for:', vendor.name);
                      priorityUpdates.push({
                        name: vendor.name,
                        oldPriority: currentPriority === 5 ? 'High' : currentPriority === 3 ? 'Medium' : 'Low',
                        newPriority: newPriority === 5 ? 'High' : newPriority === 3 ? 'Medium' : 'Low'
                      });
                    } else {
                      console.error('❌ Priority update failed for:', vendor.name);
                      const errorData = await response.json();
                      errors.push(errorData.error || `Failed to update ${vendor.name}`);
                    }
                  }
                }
              } catch (updateError) {
                console.error('❌ Priority check/update error for:', vendor.name, updateError);
                errors.push(`Failed to update ${vendor.name}: ${updateError.message}`);
              }
            } else {
              // Other errors
              const errorData = await response.json();
              errors.push(errorData.error || `Failed to save ${vendor.name}`);
            }
          }
        } catch (error) {
          errors.push(`Failed to save ${vendor.name}: ${error.message}`);
        }
      }
      
      // Show appropriate success/info messages based on scenarios
      if (results.length > 0 && errors.length === 0 && priorityUpdates.length === 0 && samePriority.length === 0) {
        // Scenario 1: All new vendors added
        showNotification('success', 'Success!', `${results.length} vendor${results.length > 1 ? 's' : ''} have been added to your dashboard. You can view them in your Dashboard under "Your Shortlisted Vendors".`);
        
        // Reset to start over with new category
        setCurrentStep(1);
        setShortlist([]);
        setVendorPriorities({});
        setSelectedCategory('');
        setCurrentVendors([]);
        
      } else if (results.length === 0 && errors.length === 0 && priorityUpdates.length === 0 && samePriority.length > 0) {
        // Scenario 2: All vendors already exist with same priority
        const vendorNames = samePriority.map(v => `${v.name} (${v.priority})`).join(', ');
        showNotification('info', 'Already Added', `${samePriority.length > 1 ? 'These vendors are' : 'This vendor is'} already in your shortlist with the same priority: ${vendorNames}`);
        
        // Reset since nothing changed
        setCurrentStep(1);
        setShortlist([]);
        setVendorPriorities({});
        setSelectedCategory('');
        setCurrentVendors([]);
        
      } else if (results.length === 0 && errors.length === 0 && priorityUpdates.length > 0 && samePriority.length === 0) {
        // Scenario 3: All vendors had priority updates
        const updateMessages = priorityUpdates.map(v => `${v.name}: ${v.oldPriority} → ${v.newPriority}`).join(', ');
        showNotification('success', 'Priorities Updated!', `Updated priorities for: ${updateMessages}`);
        
        // Reset after successful updates
        setCurrentStep(1);
        setShortlist([]);
        setVendorPriorities({});
        setSelectedCategory('');
        setCurrentVendors([]);
        
      } else {
        // Scenario 4: Mixed scenarios - create comprehensive message
        let message = '';
        let shouldReset = false;
        
        if (results.length > 0) {
          message += `✅ Added ${results.length} new vendor${results.length > 1 ? 's' : ''}: ${results.join(', ')}\n`;
          shouldReset = true;
        }
        
        if (priorityUpdates.length > 0) {
          const updateMessages = priorityUpdates.map(v => `${v.name} (${v.oldPriority} → ${v.newPriority})`).join(', ');
          message += `🔄 Updated priorities for ${priorityUpdates.length} vendor${priorityUpdates.length > 1 ? 's' : ''}: ${updateMessages}\n`;
          shouldReset = true;
        }
        
        if (samePriority.length > 0) {
          const sameMessages = samePriority.map(v => `${v.name} (${v.priority})`).join(', ');
          message += `ℹ️ Already added with same priority: ${sameMessages}\n`;
        }
        
        if (errors.length > 0) {
          message += `❌ Errors: ${errors.join(', ')}`;
        }
        
        const totalSuccess = results.length + priorityUpdates.length;
        const title = totalSuccess > 0 ? 'Completed!' : 'Already Added';
        const type = errors.length === 0 ? (totalSuccess > 0 ? 'success' : 'info') : 'warning';
        
        showNotification(type, title, message.trim());
        
        if (shouldReset && errors.length === 0) {
          // Reset if there were successful operations and no errors
          setCurrentStep(1);
          setShortlist([]);
          setVendorPriorities({});
          setSelectedCategory('');
          setCurrentVendors([]);
        }
      }
      
    } catch (error) {
      console.error('Error saving vendors to shortlist:', error);
      showNotification('error', 'Save Failed', 'There was an error saving your vendors. Please try again.');
      // Don't reset shortlist on error - keep user's selections
    } finally {
      setSaving(false);
    }
  };

  const startOver = () => {
    setCurrentStep(1);
    setShortlist([]);
    setVendorPriorities({});
    setCurrentVendors([]);
    setSelectedCategory('');
  };

  const getCategoryData = (categoryName) => {
    return categories.find(c => c.name === categoryName) || categories[0];
  };  const pageIcons = [
    { emoji: '📋', size: '6xl', opacity: '15' },
    { emoji: '✨', size: '5xl', opacity: '15' },
    { emoji: '💖', size: '3xl', opacity: '15' },
    { emoji: '💍', size: '4xl', opacity: '15' },
    { emoji: '🌸', size: '5xl', opacity: '15' },
    { emoji: '🎯', size: '6xl', opacity: '15' }
  ];



  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 relative overflow-hidden">
      <BackgroundIcons icons={pageIcons} />
      
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating sparkles */}
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-pulse opacity-10"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 2}s`
            }}
          >
            <span className="text-gold-400 text-lg">✨</span>
          </div>
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12 relative z-10">
        
        {/* Elegant Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-rose-400 via-pink-400 to-gold-400 rounded-full shadow-2xl mb-8 animate-bounce">
            <div className="relative">
              <span className="text-2xl">📋</span>
              <span className="absolute -top-1 -right-1 text-lg">💎</span>
            </div>
          </div>
          
          <h1 className="text-6xl md:text-7xl font-serif font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-gold-600 bg-clip-text text-transparent mb-6 tracking-wide">
            Vendor Shortlisting Assistant
          </h1>
          
          {/* Floral underline */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 text-gold-400">💖</div>
            </div>
          </div>
          
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed font-light">
            Streamline your vendor selection process with our intelligent shortlisting system
          </p>
        </div>

        {/* Progress Bar */}
        <div className={`mb-12 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/50 p-8">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div className={`flex items-center justify-center w-16 h-16 rounded-full border-4 transition-all duration-500 ${
                    currentStep >= step.id 
                      ? 'bg-gradient-to-r from-rose-400 to-gold-400 border-gold-300 text-white shadow-lg scale-110' 
                      : 'bg-white border-gray-300 text-gray-400'
                  }`}>
                    <span className="text-2xl">{step.icon}</span>
                  </div>
                  <div className="ml-4">
                    <div className={`font-bold ${currentStep >= step.id ? 'text-gray-800' : 'text-gray-400'}`}>
                      Step {step.id}
                    </div>
                    <div className={`text-sm ${currentStep >= step.id ? 'text-gray-600' : 'text-gray-400'}`}>
                      {step.title}
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`w-16 h-1 mx-8 rounded-full transition-all duration-500 ${
                      currentStep > step.id ? 'bg-gradient-to-r from-rose-400 to-gold-400' : 'bg-gray-200'
                    }`}></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8">
          
          {/* Main Interface */}
          <div className="col-span-1">
            <div className={`transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border-2 border-gold-200/50 p-8">
                
                {/* Step 1: Choose Category */}
                {currentStep === 1 && (
                  <div className="text-center">
                    <h3 className="text-3xl font-serif font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-gold-600 bg-clip-text text-transparent animate-pulse mb-8">
                      Choose a Vendor Category
                    </h3>
                    <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                      Select the type of wedding vendor you'd like to shortlist. We'll show you the best options in that category.
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                      {categories.map((category, index) => (
                        <div
                          key={category.name}
                          className="group relative cursor-pointer transition-all duration-500 transform hover:scale-105"
                          style={{ animationDelay: `${index * 100}ms` }}
                          onClick={() => selectCategory(category.name)}
                        >
                          {/* Magical glow effect */}
                          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-gray-200/20 to-gray-300/20 blur-lg opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
                          
                          {/* Category card */}
                          <div className={`relative bg-gradient-to-br ${category.color} rounded-2xl border-2 border-white/50 hover:border-rose-300 hover:shadow-2xl transition-all duration-500 shadow-xl backdrop-blur-sm text-center p-8 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                            
                            {/* Floating sparkles */}
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                              <div className="w-1 h-1 bg-rose-400 rounded-full animate-ping"></div>
                            </div>
                            
                            <div className="text-6xl mb-4 group-hover:scale-110 group-hover:animate-bounce transition-transform duration-300">
                              {category.icon}
                            </div>
                            <h4 className="text-xl font-bold text-gray-800 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-rose-600 group-hover:via-pink-600 group-hover:to-purple-600 transition-all duration-300">{category.name}</h4>
                            <div className="text-sm text-gray-600 mt-2">
                              {allVendors.filter(v => v.category === category.name).length} vendors available
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Loading State */}
                {loading && (
                  <div className="text-center py-20">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-rose-500 mx-auto mb-4"></div>
                    <p className="text-xl text-gray-600">Loading {selectedCategory.toLowerCase()} vendors...</p>
                  </div>
                )}

                {/* Step 2: Magical Grid View */}
                {currentStep === 2 && (
                  <div className="bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border-2 border-gold-200/50 p-8">
                    <div className="flex items-center justify-between mb-8">
                      <button 
                        onClick={() => setCurrentStep(1)} 
                        className="relative overflow-hidden bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white py-3 px-6 rounded-2xl font-bold text-base hover:shadow-2xl transform hover:scale-105 transition-all duration-300 animate-pulse"
                      >
                        <div className="flex items-center justify-center">
                          <span className="mr-2">←</span>Back to Categories
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-rose-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-2xl animate-pulse"></div>
                      </button>
                      <h3 className="text-3xl font-serif font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-gold-600 bg-clip-text text-transparent animate-pulse">{selectedCategory} Vendors</h3>
                      <div className="text-sm font-semibold bg-gradient-to-r from-gold-600 to-yellow-600 bg-clip-text text-transparent animate-pulse">
                        {getFilteredAndSortedVendors().length} vendors available
                      </div>
                    </div>
                    
                    {/* VowAI Styled Filter Options */}
                    <div className="mb-8 p-6 bg-gradient-to-r from-rose-50/80 via-pink-50/80 to-gold-50/80 rounded-2xl border-2 border-rose-200/50 shadow-lg backdrop-blur-sm">
                      <div className="flex items-center justify-between mb-6">
                        <h4 className="text-xl font-serif font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-gold-600 bg-clip-text text-transparent animate-pulse">
                          ✨ Filter & Sort Options
                        </h4>
                        <button
                          onClick={clearFilters}
                          className="px-4 py-2 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-xl font-semibold hover:from-rose-100 hover:to-pink-100 hover:text-rose-700 hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                        >
                          🔄 Clear Filters
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {/* Location Filter */}
                        <div>
                          <label className="block text-sm font-bold text-gray-700 mb-2 bg-gradient-to-r from-rose-600 to-gold-600 bg-clip-text text-transparent">
                            📍 Location
                          </label>
                          <select 
                            value={filters.location}
                            onChange={(e) => handleFilterChange('location', e.target.value)}
                            className="w-full px-4 py-3 border-2 border-rose-200 rounded-xl focus:ring-2 focus:ring-rose-300 focus:border-rose-400 transition-all duration-300 bg-white/90 backdrop-blur-sm font-medium hover:shadow-lg"
                          >
                            <option value="">All Locations</option>
                            {filterOptions.locations.map(location => (
                              <option key={location} value={location}>{location}</option>
                            ))}
                          </select>
                        </div>
                        
                        {/* Price Range Filter */}
                        <div>
                          <label className="block text-sm font-bold text-gray-700 mb-2 bg-gradient-to-r from-rose-600 to-gold-600 bg-clip-text text-transparent">
                            💰 Price Range
                          </label>
                          <select 
                            value={filters.priceRange}
                            onChange={(e) => handleFilterChange('priceRange', e.target.value)}
                            className="w-full px-4 py-3 border-2 border-rose-200 rounded-xl focus:ring-2 focus:ring-rose-300 focus:border-rose-400 transition-all duration-300 bg-white/90 backdrop-blur-sm font-medium hover:shadow-lg"
                          >
                            <option value="">All Prices</option>
                            {filterOptions.priceRanges.map(price => (
                              <option key={price} value={price}>
                                {price === '$' && '$ - Budget Friendly'}
                                {price === '$$' && '$$ - Moderate'}
                                {price === '$$$' && '$$$ - Premium'}
                                {price === '$$$$' && '$$$$ - Luxury'}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        {/* Style Filter */}
                        <div>
                          <label className="block text-sm font-bold text-gray-700 mb-2 bg-gradient-to-r from-rose-600 to-gold-600 bg-clip-text text-transparent">
                            🎨 Style
                          </label>
                          <select 
                            value={filters.style}
                            onChange={(e) => handleFilterChange('style', e.target.value)}
                            className="w-full px-4 py-3 border-2 border-rose-200 rounded-xl focus:ring-2 focus:ring-rose-300 focus:border-rose-400 transition-all duration-300 bg-white/90 backdrop-blur-sm font-medium hover:shadow-lg"
                          >
                            <option value="">All Styles</option>
                            {filterOptions.styles.map(style => (
                              <option key={style} value={style}>{style}</option>
                            ))}
                          </select>
                        </div>
                        
                        {/* Sort By */}
                        <div>
                          <label className="block text-sm font-bold text-gray-700 mb-2 bg-gradient-to-r from-rose-600 to-gold-600 bg-clip-text text-transparent">
                            🔄 Sort By
                          </label>
                          <select 
                            value={sortBy}
                            onChange={(e) => handleSortChange(e.target.value)}
                            className="w-full px-4 py-3 border-2 border-rose-200 rounded-xl focus:ring-2 focus:ring-rose-300 focus:border-rose-400 transition-all duration-300 bg-white/90 backdrop-blur-sm font-medium hover:shadow-lg"
                          >
                            <option value="rating">⭐ Highest Rated</option>
                            <option value="price-low">💰 Price: Low to High</option>
                            <option value="price-high">💎 Price: High to Low</option>
                            <option value="reviews">📝 Most Reviews</option>
                          </select>
                        </div>
                      </div>
                      
                      {/* Active Filters Display */}
                      {(filters.location || filters.priceRange || filters.style || sortBy !== 'rating') && (
                        <div className="mt-4 p-4 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 rounded-xl border border-blue-200/50">
                          <div className="flex flex-wrap gap-2 items-center">
                            <span className="text-sm font-medium text-blue-700">Active filters:</span>
                            {filters.location && (
                              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                                📍 {filters.location}
                              </span>
                            )}
                            {filters.priceRange && (
                              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                💰 {filters.priceRange}
                              </span>
                            )}
                            {filters.style && (
                              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                                🎨 {filters.style}
                              </span>
                            )}
                            {sortBy !== 'rating' && (
                              <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                                🔄 {sortBy === 'price-low' ? 'Price: Low to High' : 
                                     sortBy === 'price-high' ? 'Price: High to Low' : 
                                     sortBy === 'reviews' ? 'Most Reviews' : sortBy}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Magical Vendor Grid */}
                    <VendorGrid
                      vendors={getFilteredAndSortedVendors()}
                      category={selectedCategory}
                      onVendorClick={handleVendorClick}
                      showCompatibility={false}
                      emptyMessage={`No ${selectedCategory.toLowerCase()} vendors found with current filters`}
                      layoutMode="grid"
                    />
                    
                    {/* Bottom Navigation */}
                    <div className="flex justify-center mt-8">
                      <button 
                        onClick={() => setCurrentStep(1)} 
                        className="relative overflow-hidden bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white py-3 px-6 rounded-2xl font-bold text-base hover:shadow-2xl transform hover:scale-105 transition-all duration-300 animate-pulse"
                      >
                        <div className="flex items-center justify-center">
                          <span className="mr-2">←</span>Back to Categories
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-rose-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-2xl animate-pulse"></div>
                      </button>
                    </div>
                    
                    {/* Continue Button */}
                    {shortlist.length > 0 && (
                      <div className="text-center mt-6">
                        <button
                          onClick={() => setCurrentStep(3)}
                          className="relative overflow-hidden bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white py-4 px-8 rounded-2xl font-bold text-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 animate-pulse"
                        >
                          <div className="flex items-center justify-center">
                            <span className="mr-3">✨</span>
                            Review Shortlist ({shortlist.length} vendors)
                          </div>
                          <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-rose-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-2xl animate-pulse"></div>
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 3: Finalize Selection */}
                {currentStep === 3 && (
                  <div className="text-center">
                    <div className="flex items-center justify-between mb-8">
                      <button
                        onClick={() => {
                          setCurrentStep(2);
                        }}
                        className="relative overflow-hidden bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white py-3 px-6 rounded-2xl font-bold text-base hover:shadow-2xl transform hover:scale-105 transition-all duration-300 animate-pulse"
                      >
                        <div className="flex items-center justify-center">
                          <span className="mr-2">←</span>
                          Review More Vendors
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-rose-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-2xl animate-pulse"></div>
                      </button>
                      <h3 className="text-3xl font-serif font-bold text-gray-800">
                        Your {selectedCategory} Shortlist
                      </h3>
                      <div></div>
                    </div>
                    
                    {shortlist.length > 0 ? (
                      <div className="space-y-6">
                        <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-2xl p-6 border border-emerald-200 mb-8">
                          <div className="flex items-center justify-center space-x-4">
                            <span className="text-4xl">🎉</span>
                            <div>
                              <div className="font-bold text-emerald-700 text-xl">
                                Great choices! You've shortlisted {getShortlistSummary()}
                              </div>
                              <div className="text-emerald-600">
                                Review your selections below and finalize when ready
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="grid gap-4">
                          {shortlist.map((vendor, index) => (
                            <div
                              key={vendor.id}
                              className="flex items-center justify-between bg-gradient-to-r from-white/90 to-rose-50/90 rounded-2xl p-6 border border-gold-200 shadow-lg animate-fadeInScale"
                              style={{ animationDelay: `${index * 100}ms` }}
                            >
                              <div className="flex items-center space-x-4">
                                <div className={`w-16 h-16 bg-gradient-to-br ${getCategoryData(vendor.category).color} rounded-xl flex items-center justify-center`}>
                                  <span className="text-3xl">{vendor.image}</span>
                                </div>
                                <div className="text-left flex-1">
                                  <h4 className="font-bold text-lg text-gray-800">{vendor.name}</h4>
                                  <p className="text-gray-600">{vendor.tagline}</p>
                                  <div className="flex items-center space-x-2 mt-1">
                                    <span className="text-yellow-400 text-sm">⭐</span>
                                    <span className="text-sm font-semibold">{vendor.rating}</span>
                                    <span className="text-gray-400">•</span>
                                    <span className="text-sm text-gray-600">{vendor.location}</span>
                                    <span className="text-gray-400">•</span>
                                    <span className="text-sm font-semibold text-green-600">{vendor.price}</span>
                                  </div>
                                  <div className="flex items-center space-x-2 mt-2">
                                    <span className="text-sm font-medium text-gray-700">Priority:</span>
                                    <button
                                      onClick={() => updateVendorPriority(vendor)}
                                      className="px-3 py-1 bg-gradient-to-r from-rose-50 to-gold-50 border-2 border-rose-200 rounded-lg text-sm font-medium text-gray-800 hover:border-rose-300 hover:shadow-lg transition-all duration-300"
                                    >
                                      {vendorPriorities[vendor.id] === 5 ? '🔥 High' : 
                                       vendorPriorities[vendor.id] === 1 ? '🌱 Low' : 
                                       '⚡ Medium'}
                                    </button>
                                  </div>
                                </div>
                              </div>
                              <button
                                onClick={() => removeFromShortlist(vendor.id)}
                                className="text-red-500 hover:text-red-700 p-2 rounded-full hover:bg-red-50 transition-all duration-300"
                              >
                                <span className="text-xl">🗑️</span>
                              </button>
                            </div>
                          ))}
                        </div>
                        
                        <div className="flex gap-4 justify-center pt-6">
                          <button
                            onClick={startOver}
                            className="px-8 py-4 border-2 border-gray-300 text-gray-600 rounded-2xl font-bold hover:bg-gray-50 hover:shadow-lg transition-all duration-300"
                          >
                            🔄 Start Over
                          </button>
                          <button
                            onClick={finalizeSelection}
                            disabled={saving}
                            className="bg-gradient-to-r from-rose-500 to-gold-500 text-white px-12 py-4 rounded-2xl font-bold text-xl hover:from-rose-600 hover:to-gold-600 hover:shadow-2xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                          >
                            {saving ? (
                              <>
                                <span className="mr-3">⏳</span>
                                Saving to Dashboard...
                              </>
                            ) : (
                              <>
                                <span className="mr-3">✨</span>
                                Finalize Shortlist ({shortlist.length} vendors)
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <div className="text-6xl mb-4">📝</div>
                        <p className="text-xl text-gray-600 mb-6">No vendors in your shortlist yet</p>
                        <p className="text-gray-500 mb-8">Go back and add some {selectedCategory.toLowerCase()} vendors to your shortlist</p>
                        <div className="flex gap-4 justify-center">
                          <button
                            onClick={() => {
                              setCurrentStep(2);
                            }}
                            className="relative overflow-hidden bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white py-4 px-8 rounded-2xl font-bold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 animate-pulse"
                          >
                            <div className="flex items-center justify-center">
                              <span className="mr-2 text-xl animate-bounce">👀</span>
                              Review Vendors
                            </div>
                            <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-rose-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-2xl animate-pulse"></div>
                          </button>
                          <button
                            onClick={startOver}
                            className="px-8 py-3 border-2 border-gray-300 text-gray-600 rounded-xl font-bold hover:bg-gray-50 transition-all duration-300"
                          >
                            Start Over
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.isOpen}
        onClose={closeNotification}
        type={notification.type}
      actionLabel={notification.actionLabel}
      onAction={notification.onAction}
        title={notification.title}
        message={notification.message}
      />

      {/* Vendor Profile Modal */}
      {showProfile && selectedVendor && (
        <VendorProfileModal
          isOpen={showProfile}
          vendor={selectedVendor}
          onClose={() => setShowProfile(false)}
          source="matching"
          onShowNotification={showNotification}
        />
      )}

      {/* Priority Selection Modal - Identical to VendorProfileModal */}
      {showPriorityModal && selectedVendorForPriority && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border-2 border-rose-200/50 max-w-lg w-full">
            
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 p-6 rounded-t-3xl relative">
              <button
                onClick={() => {
                  setShowPriorityModal(false);
                  setSelectedVendorForPriority(null);
                }}
                className="absolute top-4 right-4 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300"
              >
                <span className="text-xl">✕</span>
              </button>
              
              <div className="text-center">
                <div className="text-3xl mb-2 animate-bounce">⭐</div>
                <h2 className="text-xl font-bold text-white mb-1">Set Priority for {selectedVendorForPriority.name}</h2>
                <p className="text-white/90 text-sm">Choose how important this vendor is to you</p>
              </div>
            </div>

            {/* Priority Options */}
            <div className="p-6 space-y-4">
              
              {/* High Priority */}
              <div 
                onClick={() => setSelectedPriority(5)}
                className={`cursor-pointer p-4 rounded-xl border-2 transition-all duration-300 ${
                  selectedPriority === 5 
                    ? 'border-red-300 bg-gradient-to-r from-red-50 to-pink-50 shadow-lg' 
                    : 'border-gray-200 bg-gray-50 hover:border-red-200 hover:bg-red-50'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div className="text-2xl">🔥</div>
                  <div className="flex-1">
                    <div className="font-bold text-gray-800">High Priority</div>
                    <div className="text-sm text-gray-600">Must-have vendor for your wedding</div>
                  </div>
                  <div className={`w-4 h-4 rounded-full border-2 ${
                    selectedPriority === 5 ? 'bg-red-500 border-red-500' : 'border-gray-300'
                  }`}></div>
                </div>
              </div>

              {/* Medium Priority */}
              <div 
                onClick={() => setSelectedPriority(3)}
                className={`cursor-pointer p-4 rounded-xl border-2 transition-all duration-300 ${
                  selectedPriority === 3 
                    ? 'border-yellow-300 bg-gradient-to-r from-yellow-50 to-orange-50 shadow-lg' 
                    : 'border-gray-200 bg-gray-50 hover:border-yellow-200 hover:bg-yellow-50'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div className="text-2xl">⚡</div>
                  <div className="flex-1">
                    <div className="font-bold text-gray-800">Medium Priority</div>
                    <div className="text-sm text-gray-600">Important but flexible option</div>
                  </div>
                  <div className={`w-4 h-4 rounded-full border-2 ${
                    selectedPriority === 3 ? 'bg-yellow-500 border-yellow-500' : 'border-gray-300'
                  }`}></div>
                </div>
              </div>

              {/* Low Priority */}
              <div 
                onClick={() => setSelectedPriority(1)}
                className={`cursor-pointer p-4 rounded-xl border-2 transition-all duration-300 ${
                  selectedPriority === 1 
                    ? 'border-green-300 bg-gradient-to-r from-green-50 to-teal-50 shadow-lg' 
                    : 'border-gray-200 bg-gray-50 hover:border-green-200 hover:bg-green-50'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div className="text-2xl">🌱</div>
                  <div className="flex-1">
                    <div className="font-bold text-gray-800">Low Priority</div>
                    <div className="text-sm text-gray-600">Nice to have, budget permitting</div>
                  </div>
                  <div className={`w-4 h-4 rounded-full border-2 ${
                    selectedPriority === 1 ? 'bg-green-500 border-green-500' : 'border-gray-300'
                  }`}></div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button 
                  onClick={() => {
                    setShowPriorityModal(false);
                    setSelectedVendorForPriority(null);
                  }}
                  className="flex-1 bg-gradient-to-r from-gray-200 to-gray-300 text-gray-700 py-3 px-4 rounded-xl font-bold hover:from-gray-300 hover:to-gray-400 hover:shadow-lg transition-all duration-300"
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmPriorityUpdate}
                  className="flex-1 bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 text-white py-3 px-4 rounded-xl font-bold hover:from-purple-600 hover:via-pink-600 hover:to-rose-600 hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                >
                  <span className="mr-2">⭐</span>
                  Update Priority
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        .animate-fadeInScale {
          animation: fadeInScale 0.6s ease-out;
        }
        
        .delay-200 { animation-delay: 200ms; }
        .delay-400 { animation-delay: 400ms; }
        .delay-600 { animation-delay: 600ms; }
        .delay-1000 { animation-delay: 1000ms; }
        .delay-1500 { animation-delay: 1500ms; }
        .delay-2000 { animation-delay: 2000ms; }
      `}</style>
    </div>
  );
};

export default VendorShortlistingAssistant;
